/**
 * WebOs FileSystem
 *
 * Système de fichiers virtuel basé sur IndexedDB.
 * Fournit une API asynchrone style Unix.
 */

import { createLogger } from '../utils/debug.js';

const log = createLogger('FS');

// Configuration
const DB_NAME = 'webos-fs';
const DB_VERSION = 2;  // Doit correspondre à sw-vfs.js
const STORE_NAME = 'files';

// Types de fichiers
export const FileType = {
  FILE: 'file',
  DIRECTORY: 'directory',
  SYMLINK: 'symlink'
};

/**
 * Classe principale du système de fichiers
 */
export class FileSystem {
  constructor() {
    this.db = null;
    this.ready = false;
  }

  /**
   * Initialise la connexion IndexedDB
   * @returns {Promise<void>}
   */
  async init() {
    const timer = log.time('IndexedDB init');

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        log.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.ready = true;
        timer.end();
        log.info('IndexedDB connected');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        log.info('Creating IndexedDB schema...');
        const db = event.target.result;

        // Store pour les fichiers
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'path' });
          store.createIndex('parent', 'parent', { unique: false });
          store.createIndex('type', 'type', { unique: false });
          log.debug('Object store created:', STORE_NAME);
        }
      };
    });
  }

  /**
   * Vérifie que la DB est prête
   */
  ensureReady() {
    if (!this.ready || !this.db) {
      throw new Error('FileSystem not initialized. Call init() first.');
    }
  }

  /**
   * Normalise un chemin
   * @param {string} path
   * @returns {string}
   */
  normalizePath(path) {
    // Supprimer les doubles slashes et le slash final
    let normalized = path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';

    // Résoudre . et ..
    const parts = normalized.split('/');
    const result = [];

    for (const part of parts) {
      if (part === '..') {
        result.pop();
      } else if (part !== '.' && part !== '') {
        result.push(part);
      }
    }

    return '/' + result.join('/');
  }

  /**
   * Obtient le chemin parent
   * @param {string} path
   * @returns {string}
   */
  getParentPath(path) {
    const normalized = this.normalizePath(path);
    if (normalized === '/') return null;

    const lastSlash = normalized.lastIndexOf('/');
    return lastSlash === 0 ? '/' : normalized.slice(0, lastSlash);
  }

  /**
   * Obtient le nom du fichier
   * @param {string} path
   * @returns {string}
   */
  getBasename(path) {
    const normalized = this.normalizePath(path);
    if (normalized === '/') return '';

    const lastSlash = normalized.lastIndexOf('/');
    return normalized.slice(lastSlash + 1);
  }

  /**
   * Lit un enregistrement depuis IndexedDB
   * @param {string} path
   * @returns {Promise<object|null>}
   */
  async getRecord(path) {
    this.ensureReady();
    const normalized = this.normalizePath(path);

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(normalized);

      request.onsuccess = () => {
        log.debug('getRecord:', normalized, request.result ? 'found' : 'not found');
        resolve(request.result || null);
      };

      request.onerror = () => {
        log.error('getRecord failed:', normalized, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Écrit un enregistrement dans IndexedDB
   * @param {object} record
   * @returns {Promise<void>}
   */
  async putRecord(record) {
    this.ensureReady();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(record);

      request.onsuccess = () => {
        log.debug('putRecord:', record.path, record.type);
        resolve();
      };

      request.onerror = () => {
        log.error('putRecord failed:', record.path, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Supprime un enregistrement
   * @param {string} path
   * @returns {Promise<void>}
   */
  async deleteRecord(path) {
    this.ensureReady();
    const normalized = this.normalizePath(path);

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(normalized);

      request.onsuccess = () => {
        log.debug('deleteRecord:', normalized);
        resolve();
      };

      request.onerror = () => {
        log.error('deleteRecord failed:', normalized, request.error);
        reject(request.error);
      };
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // API PUBLIQUE
  // ═══════════════════════════════════════════════════════════════

  /**
   * Vérifie si un chemin existe
   * @param {string} path
   * @returns {Promise<boolean>}
   */
  async exists(path) {
    const record = await this.getRecord(path);
    return record !== null;
  }

  /**
   * Obtient les métadonnées d'un fichier
   * @param {string} path
   * @returns {Promise<object>}
   */
  async stat(path) {
    const record = await this.getRecord(path);

    if (!record) {
      const error = new Error(`ENOENT: no such file or directory: ${path}`);
      error.code = 'ENOENT';
      throw error;
    }

    log.debug('stat:', path, { type: record.type, size: record.size });

    return {
      path: record.path,
      type: record.type,
      size: record.size || 0,
      created: record.created,
      modified: record.modified,
      target: record.target // Pour les symlinks
    };
  }

  /**
   * Lit le contenu d'un fichier
   * @param {string} path
   * @param {string} encoding - 'utf8' ou 'binary'
   * @returns {Promise<string|ArrayBuffer>}
   */
  async readFile(path, encoding = 'utf8') {
    const timer = log.time(`readFile ${path}`);
    const record = await this.getRecord(path);

    if (!record) {
      const error = new Error(`ENOENT: no such file or directory: ${path}`);
      error.code = 'ENOENT';
      throw error;
    }

    if (record.type === FileType.DIRECTORY) {
      const error = new Error(`EISDIR: illegal operation on a directory: ${path}`);
      error.code = 'EISDIR';
      throw error;
    }

    // Suivre les symlinks
    if (record.type === FileType.SYMLINK) {
      log.debug('Following symlink:', path, '->', record.target);
      return this.readFile(record.target, encoding);
    }

    timer.end();

    if (encoding === 'binary') {
      return record.data;
    }

    // Décoder en UTF-8
    if (record.data instanceof ArrayBuffer) {
      return new TextDecoder().decode(record.data);
    }

    return record.data || '';
  }

  /**
   * Écrit dans un fichier
   * @param {string} path
   * @param {string|ArrayBuffer} data
   * @param {object} options
   * @returns {Promise<void>}
   */
  async writeFile(path, data, options = {}) {
    const timer = log.time(`writeFile ${path}`);
    const normalized = this.normalizePath(path);
    const parent = this.getParentPath(normalized);

    // Vérifier que le parent existe
    if (parent) {
      const parentRecord = await this.getRecord(parent);
      if (!parentRecord) {
        if (options.createDirs) {
          await this.mkdir(parent, { recursive: true });
        } else {
          const error = new Error(`ENOENT: parent directory does not exist: ${parent}`);
          error.code = 'ENOENT';
          throw error;
        }
      }
    }

    const existing = await this.getRecord(normalized);

    // Convertir string en ArrayBuffer pour stockage uniforme
    let binaryData = data;
    if (typeof data === 'string') {
      binaryData = new TextEncoder().encode(data).buffer;
    }

    const now = Date.now();
    const record = {
      path: normalized,
      parent: parent,
      name: this.getBasename(normalized),
      type: FileType.FILE,
      data: options.append && existing ? this.appendData(existing.data, binaryData) : binaryData,
      size: binaryData.byteLength,
      created: existing?.created || now,
      modified: now
    };

    await this.putRecord(record);
    timer.end();
    log.info('File written:', normalized, `(${record.size} bytes)`);
  }

  /**
   * Concatène deux ArrayBuffers
   */
  appendData(existing, newData) {
    const existingArray = new Uint8Array(existing || new ArrayBuffer(0));
    const newArray = new Uint8Array(newData);
    const result = new Uint8Array(existingArray.length + newArray.length);
    result.set(existingArray);
    result.set(newArray, existingArray.length);
    return result.buffer;
  }

  /**
   * Liste le contenu d'un répertoire
   * @param {string} path
   * @returns {Promise<object[]>}
   */
  async readdir(path) {
    const normalized = this.normalizePath(path);
    const record = await this.getRecord(normalized);

    if (!record) {
      const error = new Error(`ENOENT: no such file or directory: ${path}`);
      error.code = 'ENOENT';
      throw error;
    }

    if (record.type !== FileType.DIRECTORY) {
      const error = new Error(`ENOTDIR: not a directory: ${path}`);
      error.code = 'ENOTDIR';
      throw error;
    }

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('parent');
      const request = index.getAll(normalized);

      request.onsuccess = () => {
        const entries = request.result.map(r => ({
          name: r.name,
          type: r.type,
          size: r.size || 0,
          modified: r.modified
        }));
        log.debug('readdir:', normalized, `(${entries.length} entries)`);
        resolve(entries);
      };

      request.onerror = () => {
        log.error('readdir failed:', normalized, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Crée un répertoire
   * @param {string} path
   * @param {object} options
   * @returns {Promise<void>}
   */
  async mkdir(path, options = {}) {
    const normalized = this.normalizePath(path);

    // Vérifier si existe déjà
    const existing = await this.getRecord(normalized);
    if (existing) {
      if (existing.type === FileType.DIRECTORY) {
        log.debug('mkdir: already exists:', normalized);
        return; // OK si c'est déjà un dossier
      }
      const error = new Error(`EEXIST: file already exists: ${path}`);
      error.code = 'EEXIST';
      throw error;
    }

    // Créer les parents si récursif
    const parent = this.getParentPath(normalized);
    if (parent && parent !== '/') {
      const parentExists = await this.exists(parent);
      if (!parentExists) {
        if (options.recursive) {
          await this.mkdir(parent, { recursive: true });
        } else {
          const error = new Error(`ENOENT: parent directory does not exist: ${parent}`);
          error.code = 'ENOENT';
          throw error;
        }
      }
    }

    const now = Date.now();
    const record = {
      path: normalized,
      parent: parent,
      name: this.getBasename(normalized),
      type: FileType.DIRECTORY,
      created: now,
      modified: now
    };

    await this.putRecord(record);
    log.info('Directory created:', normalized);
  }

  /**
   * Supprime un fichier ou répertoire
   * @param {string} path
   * @param {object} options
   * @returns {Promise<void>}
   */
  async delete(path, options = {}) {
    const normalized = this.normalizePath(path);
    const record = await this.getRecord(normalized);

    if (!record) {
      const error = new Error(`ENOENT: no such file or directory: ${path}`);
      error.code = 'ENOENT';
      throw error;
    }

    if (record.type === FileType.DIRECTORY) {
      const children = await this.readdir(normalized);

      if (children.length > 0) {
        if (!options.recursive) {
          const error = new Error(`ENOTEMPTY: directory not empty: ${path}`);
          error.code = 'ENOTEMPTY';
          throw error;
        }

        // Supprimer récursivement
        for (const child of children) {
          await this.delete(`${normalized}/${child.name}`, { recursive: true });
        }
      }
    }

    await this.deleteRecord(normalized);
    log.info('Deleted:', normalized);
  }

  /**
   * Crée un lien symbolique
   * @param {string} target - Cible du lien
   * @param {string} path - Chemin du lien
   * @returns {Promise<void>}
   */
  async symlink(target, path) {
    const normalized = this.normalizePath(path);
    const parent = this.getParentPath(normalized);

    // Vérifier que le parent existe
    if (parent) {
      const parentExists = await this.exists(parent);
      if (!parentExists) {
        const error = new Error(`ENOENT: parent directory does not exist: ${parent}`);
        error.code = 'ENOENT';
        throw error;
      }
    }

    const now = Date.now();
    const record = {
      path: normalized,
      parent: parent,
      name: this.getBasename(normalized),
      type: FileType.SYMLINK,
      target: target,
      created: now,
      modified: now
    };

    await this.putRecord(record);
    log.info('Symlink created:', normalized, '->', target);
  }

  /**
   * Lit la cible d'un lien symbolique (sans le suivre)
   * @param {string} path
   * @returns {Promise<string>}
   */
  async readlink(path) {
    const record = await this.getRecord(path);

    if (!record) {
      const error = new Error(`ENOENT: no such file or directory: ${path}`);
      error.code = 'ENOENT';
      throw error;
    }

    if (record.type !== FileType.SYMLINK) {
      const error = new Error(`EINVAL: not a symbolic link: ${path}`);
      error.code = 'EINVAL';
      throw error;
    }

    return record.target;
  }

  /**
   * Résout un lien symbolique
   * @param {string} path
   * @param {number} maxDepth
   * @returns {Promise<string>}
   */
  async resolveSymlink(path, maxDepth = 10) {
    let current = this.normalizePath(path);
    let depth = 0;

    while (depth < maxDepth) {
      const record = await this.getRecord(current);

      if (!record) {
        const error = new Error(`ENOENT: no such file or directory: ${current}`);
        error.code = 'ENOENT';
        throw error;
      }

      if (record.type !== FileType.SYMLINK) {
        return current;
      }

      current = record.target;
      depth++;
      log.debug('Resolving symlink:', path, '->', current, `(depth: ${depth})`);
    }

    const error = new Error(`ELOOP: too many levels of symbolic links: ${path}`);
    error.code = 'ELOOP';
    throw error;
  }

  // ═══════════════════════════════════════════════════════════════
  // OPÉRATIONS DE MANIPULATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Renomme/déplace un fichier ou répertoire (opération atomique)
   * @param {string} oldPath - Chemin source
   * @param {string} newPath - Chemin destination
   * @returns {Promise<void>}
   */
  async rename(oldPath, newPath) {
    const timer = log.time(`rename ${oldPath} -> ${newPath}`);
    const normalizedOld = this.normalizePath(oldPath);
    const normalizedNew = this.normalizePath(newPath);

    // Vérifier que la source existe
    const oldRecord = await this.getRecord(normalizedOld);
    if (!oldRecord) {
      const error = new Error(`ENOENT: no such file or directory: ${oldPath}`);
      error.code = 'ENOENT';
      throw error;
    }

    // Vérifier que la destination n'existe pas
    const newRecord = await this.getRecord(normalizedNew);
    if (newRecord) {
      const error = new Error(`EEXIST: file already exists: ${newPath}`);
      error.code = 'EEXIST';
      throw error;
    }

    // Vérifier que le parent de la destination existe
    const newParent = this.getParentPath(normalizedNew);
    if (newParent) {
      const parentExists = await this.exists(newParent);
      if (!parentExists) {
        const error = new Error(`ENOENT: parent directory does not exist: ${newParent}`);
        error.code = 'ENOENT';
        throw error;
      }
    }

    // Si c'est un répertoire, renommer récursivement tous les enfants
    if (oldRecord.type === FileType.DIRECTORY) {
      await this.renameDirectory(normalizedOld, normalizedNew);
    } else {
      // Pour un fichier ou symlink, simplement mettre à jour le record
      const now = Date.now();
      const newRecordData = {
        ...oldRecord,
        path: normalizedNew,
        parent: newParent,
        name: this.getBasename(normalizedNew),
        modified: now
      };

      // Transaction atomique : créer le nouveau puis supprimer l'ancien
      await this.putRecord(newRecordData);
      await this.deleteRecord(normalizedOld);
    }

    timer.end();
    log.info('Renamed:', normalizedOld, '->', normalizedNew);
  }

  /**
   * Renomme récursivement un répertoire et son contenu
   * @param {string} oldDir
   * @param {string} newDir
   */
  async renameDirectory(oldDir, newDir) {
    const oldRecord = await this.getRecord(oldDir);
    const newParent = this.getParentPath(newDir);
    const now = Date.now();

    // Créer le nouveau répertoire
    const newDirRecord = {
      ...oldRecord,
      path: newDir,
      parent: newParent,
      name: this.getBasename(newDir),
      modified: now
    };
    await this.putRecord(newDirRecord);

    // Renommer récursivement tous les enfants
    const children = await this.readdir(oldDir);
    for (const child of children) {
      const oldChildPath = `${oldDir}/${child.name}`;
      const newChildPath = `${newDir}/${child.name}`;

      if (child.type === FileType.DIRECTORY) {
        await this.renameDirectory(oldChildPath, newChildPath);
      } else {
        const childRecord = await this.getRecord(oldChildPath);
        const newChildRecord = {
          ...childRecord,
          path: newChildPath,
          parent: newDir,
          modified: now
        };
        await this.putRecord(newChildRecord);
        await this.deleteRecord(oldChildPath);
      }
    }

    // Supprimer l'ancien répertoire
    await this.deleteRecord(oldDir);
  }

  /**
   * Copie un fichier
   * @param {string} src - Chemin source
   * @param {string} dest - Chemin destination
   * @param {Object} options - { recursive: boolean }
   * @returns {Promise<void>}
   */
  async copy(src, dest, options = {}) {
    const timer = log.time(`copy ${src} -> ${dest}`);
    const normalizedSrc = this.normalizePath(src);
    const normalizedDest = this.normalizePath(dest);

    const srcRecord = await this.getRecord(normalizedSrc);
    if (!srcRecord) {
      const error = new Error(`ENOENT: no such file or directory: ${src}`);
      error.code = 'ENOENT';
      throw error;
    }

    // Vérifier que le parent de la destination existe
    const destParent = this.getParentPath(normalizedDest);
    if (destParent) {
      const parentExists = await this.exists(destParent);
      if (!parentExists) {
        const error = new Error(`ENOENT: parent directory does not exist: ${destParent}`);
        error.code = 'ENOENT';
        throw error;
      }
    }

    if (srcRecord.type === FileType.DIRECTORY) {
      if (!options.recursive) {
        const error = new Error(`EISDIR: cannot copy directory without recursive option: ${src}`);
        error.code = 'EISDIR';
        throw error;
      }
      await this.copyDirectory(normalizedSrc, normalizedDest);
    } else {
      // Copier fichier ou symlink
      const now = Date.now();
      const destRecord = {
        ...srcRecord,
        path: normalizedDest,
        parent: destParent,
        name: this.getBasename(normalizedDest),
        created: now,
        modified: now
      };
      await this.putRecord(destRecord);
    }

    timer.end();
    log.info('Copied:', normalizedSrc, '->', normalizedDest);
  }

  /**
   * Copie récursivement un répertoire
   * @param {string} srcDir
   * @param {string} destDir
   */
  async copyDirectory(srcDir, destDir) {
    const srcRecord = await this.getRecord(srcDir);
    const destParent = this.getParentPath(destDir);
    const now = Date.now();

    // Créer le répertoire destination
    const destDirRecord = {
      path: destDir,
      parent: destParent,
      name: this.getBasename(destDir),
      type: FileType.DIRECTORY,
      created: now,
      modified: now
    };
    await this.putRecord(destDirRecord);

    // Copier récursivement les enfants
    const children = await this.readdir(srcDir);
    for (const child of children) {
      const srcChildPath = `${srcDir}/${child.name}`;
      const destChildPath = `${destDir}/${child.name}`;

      if (child.type === FileType.DIRECTORY) {
        await this.copyDirectory(srcChildPath, destChildPath);
      } else {
        const childRecord = await this.getRecord(srcChildPath);
        const destChildRecord = {
          ...childRecord,
          path: destChildPath,
          parent: destDir,
          created: now,
          modified: now
        };
        await this.putRecord(destChildRecord);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // MÉTHODES UTILITAIRES
  // ═══════════════════════════════════════════════════════════════

  /**
   * Retourne tous les fichiers (pour debug)
   * @returns {Promise<object[]>}
   */
  async listAll() {
    this.ensureReady();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Vide tout le système de fichiers (pour debug/reset)
   * @returns {Promise<void>}
   */
  async clear() {
    this.ensureReady();
    log.warn('Clearing entire filesystem!');

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        log.info('Filesystem cleared');
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}

// Instance singleton
let instance = null;

/**
 * Obtient l'instance du FileSystem
 * @returns {FileSystem}
 */
export function getFileSystem() {
  if (!instance) {
    instance = new FileSystem();
  }
  return instance;
}

// Exposer pour debug dans la console
if (typeof window !== 'undefined') {
  window.__WEBOS_FS__ = {
    getInstance: getFileSystem
  };
}
